import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {trackEventWithModule} from 'analytics/Analytics';
import {UPDATE_FLOWCHART_MODAL_TYPE} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {
  ActionOrigin,
  ActionMethod,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import HelpToolTip from 'help/HelpTooltip';
import ProcessFlowchartViewer from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartViewer';
import {ProcessInteractiveFlowchart} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import validateProcessInteractiveFlowchart from 'processes/graph-builder-processes/flowchart-viz/util/validateProcessInteractiveFlowchart';
import constructDesignSpace from 'processes/graph-builder-processes/util/constructDesignSpace';
import {extractGraphBuilderErrorTypes} from 'processes/settings/graph-builder-flowchart-config/getFlowchartConfigValidationResults';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY_DARK, GRAY_LIGHT, RED} from 'ui-library/Colors';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import runModal from 'ui-library/runModal';

import {ProcessTaskSchemaGraphQL} from '../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {
  AvailableTaskSchema,
  isPlaceholderTaskSchema,
  ClientSchemaMapperConfig,
} from './FlowchartConfigConstants';
import TemplateFlowchartEditorModal from './TemplateFlowchartEditorModal';

export interface TemplateFlowchartPaneProps {
  isEditing: boolean;
  templateFlowchart: ProcessInteractiveFlowchart | null;
  availableTaskSchemas: Array<AvailableTaskSchema>;
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>;
  rootTaskSchema: ProcessTaskSchemaGraphQL;
  didInitialFlowchartHaveChainedRouters: boolean;
  isFlowchartModifiableOnCreation: boolean;
  isFlowchartModifiableAfterCreation: boolean;
  onChangeTemplateFlowchart: (flowchart: ProcessInteractiveFlowchart | null) => void;
  onToggleIsFlowchartModifiableOnCreation: (isFlowchartModifiableOnCreation: boolean) => void;
  onToggleIsFlowchartModifiableAfterCreation: (isFlowchartModifiableAfterCreation: boolean) => void;
  eventGroupId: string;
}

interface EditTemplateFlowchartSectionProps {
  isEditing: boolean;
  templateFlowchart: ProcessInteractiveFlowchart | null;
  availableTaskSchemas: Array<AvailableTaskSchema>;
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>;
  rootTaskSchema: ProcessTaskSchemaGraphQL;
  didInitialFlowchartHaveChainedRouters: boolean;
  onChangeTemplateFlowchart: (flowchart: ProcessInteractiveFlowchart | null) => void;
  onToggleIsFlowchartModifiableOnCreation: (isFlowchartModifiableOnCreation: boolean) => void;
  eventGroupId: string;
}

function EditTemplateFlowchartSection(props: EditTemplateFlowchartSectionProps): JSX.Element {
  const designSpace = useMemo(
    () =>
      constructDesignSpace(
        props.availableTaskSchemas.filter(
          (availableTaskSchema) => !isPlaceholderTaskSchema(availableTaskSchema)
        ) as Array<ProcessTaskSchemaGraphQL>,
        props.schemaMapperConfigs,
        props.rootTaskSchema
      ),
    [props.availableTaskSchemas, props.schemaMapperConfigs, props.rootTaskSchema]
  );

  const openFlowchartConfigEditor = async (): Promise<void> => {
    trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
      event_group_id: props.eventGroupId,
      action_origin: ActionOrigin.SETTINGS_TASK_SCHEMA_PAGE_FLOWCHART_CONFIGURATION,
      action_method: ActionMethod.TEMPLATE_FLOWCHART_MODIFICATION,
      task_schema_api_id: props.rootTaskSchema.id,
      error_types: props.templateFlowchart
        ? extractGraphBuilderErrorTypes(
            validateProcessInteractiveFlowchart(
              props.templateFlowchart,
              designSpace,
              props.didInitialFlowchartHaveChainedRouters
            )
          )
        : null,
      modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
    });

    const newFlowchart = await runModal(TemplateFlowchartEditorModal, {
      flowchart: props.templateFlowchart,
      designSpace,
      eventGroupId: props.eventGroupId,
      didInitialFlowchartHaveChainedRouters: props.didInitialFlowchartHaveChainedRouters,
    });
    if (newFlowchart != null) {
      props.onChangeTemplateFlowchart(newFlowchart);
    }
  };

  const disableEditButton = props.availableTaskSchemas.length === 0 || props.schemaMapperConfigs.length === 0;
  const disabledEditButtonTooltipText = 'Cannot edit flowchart with no task schemas or transitions';

  if (props.templateFlowchart == null) {
    return props.isEditing ? (
      <Tooltip title={disabledEditButtonTooltipText} disabled={!disableEditButton}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MEDIUM}
          text="Open flowchart editor"
          onClick={openFlowchartConfigEditor}
          disabled={disableEditButton}
          dataTestAttrs={{component: 'TemplateFlowchartPane', element: 'openFlowchartEditorButton'}}
        />
      </Tooltip>
    ) : (
      <span>No template flowchart configured</span>
    );
  }
  return (
    <div className={css(styles.flowchartEditorWrapper)}>
      <ProcessFlowchartViewer
        flowchart={props.templateFlowchart}
        designSpace={designSpace}
        reactFlowEmbeddedElement={
          props.isEditing ? (
            <div className={css(styles.buttonsInReactFlowWrapper)}>
              <div className={css(styles.buttonWrapper)}>
                <Tooltip
                  title={disableEditButton ? disabledEditButtonTooltipText : 'Edit template flowchart'}
                >
                  <Button
                    buttonStyle={ButtonStyle.LINK}
                    icon="edit"
                    className={css(styles.editButton)}
                    onClick={openFlowchartConfigEditor}
                    disabled={disableEditButton}
                    dataTestAttrs={{component: 'TemplateFlowchartPane', element: 'openFlowchartEditorButton'}}
                  />
                </Tooltip>
                <Tooltip title="Remove template flowchart">
                  <Button
                    buttonStyle={ButtonStyle.LINK}
                    icon="delete"
                    className={css(styles.deleteButton)}
                    onClick={() => {
                      props.onChangeTemplateFlowchart(null);
                      props.onToggleIsFlowchartModifiableOnCreation(true);
                    }}
                    dataTestAttrs={{component: 'TemplateFlowchartPane', element: 'removeFlowchart'}}
                  />
                </Tooltip>
              </div>
            </div>
          ) : null
        }
        editorEventGroupId={props.eventGroupId}
        actionMethod={ActionMethod.TEMPLATE_FLOWCHART_MODIFICATION}
        didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
      />
    </div>
  );
}

export default function TemplateFlowchartPane(props: TemplateFlowchartPaneProps): JSX.Element {
  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'TemplateFlowchartPane', element: 'root'})}
    >
      <EditTemplateFlowchartSection
        isEditing={props.isEditing}
        templateFlowchart={props.templateFlowchart}
        availableTaskSchemas={props.availableTaskSchemas}
        schemaMapperConfigs={props.schemaMapperConfigs}
        rootTaskSchema={props.rootTaskSchema}
        didInitialFlowchartHaveChainedRouters={props.didInitialFlowchartHaveChainedRouters}
        onChangeTemplateFlowchart={props.onChangeTemplateFlowchart}
        onToggleIsFlowchartModifiableOnCreation={props.onToggleIsFlowchartModifiableOnCreation}
        eventGroupId={props.eventGroupId}
      />
      <div className={css(styles.checkboxSection)}>
        <div className={css(styles.checkboxWrapper)}>
          <CheckboxInput
            id="isFlowchartModifiableOnCreation"
            className={css(styles.checkbox)}
            checked={props.isFlowchartModifiableOnCreation}
            label="Users can modify the flowchart when creating tasks"
            onChange={(e) => {
              props.onToggleIsFlowchartModifiableOnCreation(e.target.checked);
            }}
            disabled={!props.isEditing || !props.templateFlowchart}
            dataTestAttrs={{
              component: 'TemplateFlowchartPane',
              element: 'isFlowchartModifiableOnCreationCheckbox',
            }}
          />
          <HelpToolTip
            className="u-mini-spacing-left"
            text="If no template flowchart is specified, users must define the flowchart when creating tasks"
          />
        </div>
        <div className={css(styles.checkboxWrapper)}>
          <CheckboxInput
            id="isFlowchartModifiableAfterCreation"
            className={css(styles.checkbox)}
            checked={props.isFlowchartModifiableAfterCreation}
            label="Users can modify the flowchart after tasks have been created"
            onChange={(e) => {
              props.onToggleIsFlowchartModifiableAfterCreation(e.target.checked);
            }}
            disabled={!props.isEditing}
            dataTestAttrs={{
              component: 'TemplateFlowchartPane',
              element: 'isFlowchartModifiableAfterCreationCheckbox',
            }}
          />
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    margin: '16px',
  },
  flowchartEditorWrapper: {
    height: '500px',
  },
  checkboxWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkboxSection: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    marginTop: '12px',
    marginBottom: '12px',
    paddingTop: '12px',
  },
  checkbox: {
    marginTop: '12px',
    marginBottom: '12px',
  },
  buttonsInReactFlowWrapper: {
    position: 'absolute',
    right: '8px',
    top: '0',
    zIndex: 5,
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  editButton: {
    color: GRAY_DARK,
    padding: '8px',
    boxShadow: 'none',
    ':hover': {
      color: BLUE_LIGHT,
    },
  },
  deleteButton: {
    color: GRAY_DARK,
    padding: '8px',
    boxShadow: 'none',
    ':hover': {
      color: RED,
    },
  },
});
